// ─────────────────────────────────────────────────────────────────────────────
// moon-engine.js — The Stars Above / moon phase engine
//
//   MoonEngine.compute(date, lat, lon)  -> { phase, fraction, age, frame, rotation, name }
//   MoonEngine.renderSquare(state, px)  -> Promise<canvas>  (drop into the poster slot)
//
// The maths is the standard Meeus/SunCalc set. Verified against moonphaseprints'
// own getMoon endpoint: our phase matched theirs to 14 decimal places
// (0.44553649602788 for 2026-01-01T22:00Z), and their frame index round(phase*100)
// matched the asset they served.
//
// Frames: NASA SVS "Moon Phases Loop" (ID 4310) by Ernie Wright, built from LRO
// LOLA elevation + LROC WAC imagery. 236 frames = one synodic month at 3-hour
// steps. Credit: "NASA's Scientific Visualization Studio".
//   frame = round(phase * 236) + 1, wrapping at 236.  phase 0.5 -> frame 119 = full.
//
// Unlike the star map this needs NO data files — phase is pure maths from the date.
// ─────────────────────────────────────────────────────────────────────────────
(function (window) {
  'use strict';

  // ── Frames ─────────────────────────────────────────────────────────────────
  // The look (lit exposure, ambient dark side, bloom, glow) is BAKED into the frames
  // by bake_moons.py — the same approach moonphaseprints use. So there's nothing to
  // composite at runtime: no ctx.filter, no gradients, nothing to break on Safari or
  // a slow phone. Pick a frame, rotate it, draw it.
  var MOON_BASE   = 'https://pub-cb76d8ae32574abe97650946e8421f75.r2.dev/moons/';
  var FRAMES      = 236;
  var FULL_FRAME  = 119;      // phase 0.5 — kept for reference/preloading
  // Rotation is two corrections, not one:
  //
  //  1. NASA's loop is IDEALISED — it ignores subsolar latitude, so its terminator
  //     sits at exactly +/-90 deg (lit dead-left when waning, dead-right when waxing).
  //     The real bright limb is a little off that, so we rotate by the difference.
  //     SunCalc's `angle` uses the same convention (negative waxing, positive waning)
  //     and sits near +/-90, which makes the correction just (angle -/+ 90).
  //  2. Then tilt the geocentric view into what an observer at this lat/lon actually
  //     saw — the parallactic angle.
  //
  // moonphaseprints apply the FULL limb angle because their asset has a fixed vertical
  // terminator. Copying that onto NASA frames double-applies it and lands ~180 out.
  var ROT_OFFSET  = 0;    // nudge if the frames ever sit differently

  // ROT_MODE decides what "correct" means here, and it's a judgement call:
  //   'none'     — north up, exactly as NASA renders it and as the moon looks in
  //                photographs. Stable: the same date always gives the same artwork.
  //   'observed' — tilted as it actually appeared from that lat/lon at that minute.
  //                More "true", but the tilt swings ~5.5 deg per HOUR, so a customer
  //                guessing at the time silently rotates their print. This is why no
  //                two moon-print sites agree on the angle.
  // Default 'none' — for a poster, consistency beats a precision nobody can verify.
  var ROT_MODE    = 'none';

  var rad = Math.PI / 180, dayMs = 86400000, J1970 = 2440588, J2000 = 2451545,
      e = rad * 23.4397, SYNODIC = 29.530588853;

  function toDays(date) { return (date.valueOf() / dayMs - 0.5 + J1970) - J2000; }
  function ra(l, b) { return Math.atan2(Math.sin(l) * Math.cos(e) - Math.tan(b) * Math.sin(e), Math.cos(l)); }
  function dec(l, b) { return Math.asin(Math.sin(b) * Math.cos(e) + Math.cos(b) * Math.sin(e) * Math.sin(l)); }
  function siderealTime(d, lw) { return rad * (280.16 + 360.9856235 * d) - lw; }

  function sunCoords(d) {
    var M = rad * (357.5291 + 0.98560028 * d),
        C = rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M)),
        L = M + C + rad * 102.9372 + Math.PI;
    return { dec: dec(L, 0), ra: ra(L, 0) };
  }

  function moonCoords(d) {
    var L = rad * (218.316 + 13.176396 * d),
        M = rad * (134.963 + 13.064993 * d),
        F = rad * (93.272 + 13.229350 * d),
        l = L + rad * 6.289 * Math.sin(M),
        b = rad * 5.128 * Math.sin(F),
        dt = 385001 - 20905 * Math.cos(M);
    return { ra: ra(l, b), dec: dec(l, b), dist: dt };
  }

  // How far the real bright limb sits from NASA's idealised +/-90 deg terminator.
  function limbCorrection(angle) {
    var ideal = (angle < 0 ? -Math.PI/2 : Math.PI/2);
    return angle - ideal;
  }

  function phaseName(p) {
    if (p < 0.03 || p > 0.97) return 'New Moon';
    if (p < 0.22) return 'Waxing Crescent';
    if (p < 0.28) return 'First Quarter';
    if (p < 0.47) return 'Waxing Gibbous';
    if (p < 0.53) return 'Full Moon';
    if (p < 0.72) return 'Waning Gibbous';
    if (p < 0.78) return 'Last Quarter';
    return 'Waning Crescent';
  }

  function frameFor(phase) {
    return ((Math.round(phase * FRAMES) % FRAMES) + FRAMES) % FRAMES + 1;
  }

  function frameUrl(n) {
    return MOON_BASE + 'moon_' + String(n).replace(/^(\d)$/, '00$1').replace(/^(\d\d)$/, '0$1') + '.webp';
  }

  // Everything the poster needs, from a moment and a place.
  function compute(date, lat, lon) {
    var d = toDays(date), s = sunCoords(d), m = moonCoords(d), sdist = 149598000;

    var phi = Math.acos(Math.sin(s.dec) * Math.sin(m.dec) +
                        Math.cos(s.dec) * Math.cos(m.dec) * Math.cos(s.ra - m.ra));
    var inc = Math.atan2(sdist * Math.sin(phi), m.dist - sdist * Math.cos(phi));
    var angle = Math.atan2(Math.cos(s.dec) * Math.sin(s.ra - m.ra),
                           Math.sin(s.dec) * Math.cos(m.dec) -
                           Math.cos(s.dec) * Math.sin(m.dec) * Math.cos(s.ra - m.ra));

    // Parallactic angle — how the moon is tilted as seen from THIS place at THIS time.
    // Skylit and Stars In Hands only offer a crude N/S flip; this is the real thing.
    var lw = rad * -lon, latR = rad * lat, H = siderealTime(d, lw) - m.ra;
    var pa = Math.atan2(Math.sin(H), Math.tan(latR) * Math.cos(m.dec) - Math.sin(m.dec) * Math.cos(H));

    var phase = 0.5 + 0.5 * inc * (angle < 0 ? -1 : 1) / Math.PI;

    return {
      phase: phase,
      fraction: (1 + Math.cos(inc)) / 2,      // illuminated fraction, 0-1
      age: phase * SYNODIC,                   // days into the cycle
      distance: m.dist,                       // km
      frame: frameFor(phase),
      rotation: (ROT_MODE === 'observed')
        ? (limbCorrection(angle) - pa) / rad + ROT_OFFSET
        : ROT_OFFSET,
      name: phaseName(phase)
    };
  }

  // ── Frame loading ──────────────────────────────────────────────────────────
  var cache = {};
  function loadFrame(n) {
    var url = frameUrl(n);
    if (cache[url]) return Promise.resolve(cache[url]);
    return new Promise(function (resolve, reject) {
      var im = new Image();
      // Required, or drawing these onto the print canvas taints it and toBlob throws
      // — which would mean no print file and a stuck order. The R2 bucket has a CORS
      // policy allowing GET from the store origin.
      im.crossOrigin = 'anonymous';
      im.onload = function () { cache[url] = im; resolve(im); };
      im.onerror = function () { reject(new Error('moon frame failed: ' + url)); };
      im.src = url;
    });
  }

  function preload(date, lat, lon) {
    var info = compute(date, lat, lon);
    return loadFrame(info.frame).then(function () { return info; });
  }

  // Render the moon into a square canvas, ready for the poster slot.
  // px = the square's size in pixels (small for the editor, print-res for the print).
  // The frame is already finished art, so this is just: draw it, rotated.
  function renderSquare(state, px) {
    var info = compute(state.date, state.lat, state.lon);

    return loadFrame(info.frame).then(function (frame) {
      var cv = document.createElement('canvas');
      cv.width = px; cv.height = px;
      var ctx = cv.getContext('2d');
      var half = px / 2;

      ctx.save();
      ctx.translate(half, half);
      // Tilt the moon the way it actually appeared from this place at this moment.
      // Skylit and Stars In Hands only offer a crude N/S flip; this is the real angle.
      if (state.rotate !== false) ctx.rotate(info.rotation * rad);
      ctx.drawImage(frame, -half, -half, px, px);
      ctx.restore();

      return { canvas: cv, info: info };
    });
  }

  window.MoonEngine = {
    compute: compute,
    frameFor: frameFor,
    frameUrl: frameUrl,
    preload: preload,
    renderSquare: renderSquare,
    FRAMES: FRAMES,
    FULL_FRAME: FULL_FRAME
  };
})(window);